import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { useRoutePersistence } from "@/hooks/use-route-persistence";
import { AuthProvider } from "@/lib/auth-context";
import { TenantProvider, TenantLoader } from "@/lib/tenant-context";
import { WizardProvider } from "@/lib/wizard-context";
import ProtectedRoute from "@/components/ProtectedRoute";
import ConsumerPageContainer from "@/components/ConsumerPageContainer";
import RouteFallback from "@/components/RouteFallback";
import SessionScopeGuard from "@/components/SessionScopeGuard";
import SupportWidgetMount from "@/components/support/SupportWidgetMount";
import AttributionBoot from "@/components/AttributionBoot";
import { withDeployReload } from "@/lib/chunk-reload";

/* Every lazy page below is wrapped in withDeployReload: a split file requested
   by a tab that was open across a publish reloads ONCE instead of hitting the
   error boundary, because the new build is already being served. The loop guard
   lives in that helper, so a genuinely broken chunk still reaches a human. */



/* ────────────────────────────────────────────────────────────────
   EAGER — the consumer revenue path stays in the main chunk.
   A consumer landing on / or /create must never pay a lazy hop.
   ──────────────────────────────────────────────────────────────── */
import Index from "./pages/Index";
import TenantLanding from "./pages/TenantLanding";
import ConsentFlow from "./pages/ConsentFlow";
import CardConfigurator from "./pages/CardConfigurator";
import CardEditor from "./pages/CardEditor";
import SubmissionFlow from "./pages/SubmissionFlow";
import WaitlistLanding from "./pages/WaitlistLanding";
import UserStatus from "./pages/UserStatus";
import UserAccount from "./pages/UserAccount";
import S9Account from "./pages/consumer/S9Account";
import S9bStanding from "./pages/consumer/S9bStanding";
import S10Creator from "./pages/consumer/S10Creator";
import SharedCard from "./pages/SharedCard";
import ConsumerFlow from "./pages/ConsumerFlow";
import LegacyConsumerRedirect from "./pages/LegacyConsumerRedirect";
import PolicyPage from "./pages/PolicyPage";
import LegalDocumentPage from "./pages/LegalDocumentPage";
import CreditPurchaseSuccess from "./pages/CreditPurchaseSuccess";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

/* ────────────────────────────────────────────────────────────────
   LAZY — staff surface. /admin/* and /platform/* (plus their sidebar
   shells and recharts/jszip dependencies) are code-split out of the
   consumer entry chunk.
   ──────────────────────────────────────────────────────────────── */
const SupportThread = lazy(withDeployReload(() => import("./pages/SupportThread")));

const AdminLayout = lazy(withDeployReload(() => import("@/components/AdminLayout")));
const PlatformLayout = lazy(withDeployReload(() => import("@/components/PlatformLayout")));

const AdminResetPassword = lazy(withDeployReload(() => import("./pages/AdminResetPassword")));
const AdminAcceptInvite = lazy(withDeployReload(() => import("./pages/AdminAcceptInvite")));
const AdminDashboard = lazy(withDeployReload(() => import("./pages/AdminDashboard")));
const AdminReviewQueue = lazy(withDeployReload(() => import("./pages/AdminReviewQueue")));
const AdminReview = lazy(withDeployReload(() => import("./pages/AdminReview")));
const AdminTenantConfig = lazy(withDeployReload(() => import("./pages/AdminTenantConfig")));
const AdminConfigWizard = lazy(withDeployReload(() => import("./pages/AdminConfigWizard")));
const AdminCardLayout = lazy(withDeployReload(() => import("./pages/AdminCardLayout")));
const AdminAccount = lazy(withDeployReload(() => import("./pages/AdminAccount")));
const AdminPromptTemplates = lazy(withDeployReload(() => import("./pages/AdminPromptTemplates")));
const AdminPolicyTemplates = lazy(withDeployReload(() => import("./pages/AdminPolicyTemplates")));
const AdminEmailTemplates = lazy(withDeployReload(() => import("./pages/AdminEmailTemplates")));
const PlatformBroadcasts = lazy(withDeployReload(() => import("./pages/PlatformBroadcasts")));
const PlatformSupportArticles = lazy(withDeployReload(() => import("./pages/PlatformSupportArticles")));
const AdminCreditPacks = lazy(withDeployReload(() => import("./pages/AdminCreditPacks")));
const AdminTenants = lazy(withDeployReload(() => import("./pages/AdminTenants")));
const AdminMembers = lazy(withDeployReload(() => import("./pages/AdminMembers")));
const AdminPricing = lazy(withDeployReload(() => import("./pages/AdminPricing")));
const AdminHelpGuide = lazy(withDeployReload(() => import("./pages/AdminHelpGuide")));

const PlatformAnalytics = lazy(withDeployReload(() => import("./pages/PlatformAnalytics")));
const PlatformOpsDashboard = lazy(withDeployReload(() => import("./pages/PlatformOpsDashboard")));
const PlatformFindings = lazy(withDeployReload(() => import("./pages/PlatformFindings")));

const PlatformCustomers = lazy(withDeployReload(() => import("./pages/PlatformCustomers")));

const PlatformProgramConfig = lazy(withDeployReload(() => import("./pages/PlatformProgramConfig")));
const PlatformConfigResolution = lazy(withDeployReload(() => import("./pages/PlatformConfigResolution")));
const PlatformTenantUsage = lazy(withDeployReload(() => import("./pages/PlatformTenantUsage")));
const PlatformUsageEvents = lazy(withDeployReload(() => import("./pages/PlatformUsageEvents")));
const PlatformSettings = lazy(withDeployReload(() => import("./pages/PlatformSettings")));
const PlatformHandoff = lazy(withDeployReload(() => import("./pages/PlatformHandoff")));
const PlatformRoutingRules = lazy(withDeployReload(() => import("./pages/PlatformRoutingRules")));
const PlatformWebsiteManagement = lazy(withDeployReload(() => import("./pages/PlatformWebsiteManagement")));
const PlatformMarketingReel = lazy(withDeployReload(() => import("./pages/PlatformMarketingReel")));
const PlatformSelfieLibrary = lazy(withDeployReload(() => import("./pages/PlatformSelfieLibrary")));
const PlatformSelfieFocus = lazy(withDeployReload(() => import("./pages/PlatformSelfieFocus")));
const PlatformStyleGallery = lazy(withDeployReload(() => import("./pages/PlatformStyleGallery")));
const PlatformContentGeneration = lazy(withDeployReload(() => import("./pages/PlatformContentGeneration")));
const PlatformOperatorLookup = lazy(withDeployReload(() => import("./pages/PlatformOperatorLookup")));
const PlatformAdminRoster = lazy(withDeployReload(() => import("./pages/PlatformAdminRoster")));
const PlatformOperatorApprovals = lazy(withDeployReload(() => import("./pages/PlatformOperatorApprovals")));
const PlatformLegalRegister = lazy(withDeployReload(() => import("./pages/PlatformLegalRegister")));
const PlatformAuditLog = lazy(withDeployReload(() => import("./pages/PlatformAuditLog")));

const queryClient = new QueryClient();

const RoutePersistence = () => { useRoutePersistence(); return null; };

/** Suspense boundary for one lazy route element. Nested inside the sidebar
 *  shells, so the chrome stays mounted while a page chunk arrives. */
const S = (el: ReactNode) => <Suspense fallback={<RouteFallback />}>{el}</Suspense>;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <TenantProvider>
          <WizardProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <RoutePersistence />
              <SessionScopeGuard />
              {/* §1 analytics: first-touch UTM/ref capture on ANY landing route. */}
              <AttributionBoot />

              {/* Consumer help affordance — excluded on staff surfaces. */}
              <SupportWidgetMount />
              <Routes>
                {/* Marketing landing page */}
                <Route path="/" element={<Index />} />

                {/* Consumer flow */}
                <Route path="/t/:tenant_slug" element={<TenantLoader><ConsumerPageContainer><TenantLanding /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/consent" element={<TenantLoader><ConsumerPageContainer><ConsentFlow /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/configure" element={<TenantLoader><ConsumerPageContainer><CardConfigurator /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/editor" element={<TenantLoader><ConsumerPageContainer><CardEditor /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/submit" element={<TenantLoader><ConsumerPageContainer><SubmissionFlow /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/waitlist" element={<TenantLoader><ConsumerPageContainer><WaitlistLanding /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/status" element={<TenantLoader><ConsumerPageContainer><UserStatus /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/account" element={<TenantLoader><ConsumerPageContainer><UserAccount /></ConsumerPageContainer></TenantLoader>} />
                {/*
                  TENANT white-label policies (the BANK is controller, merge
                  fields are correct there). These are NOT Cardmoji's register
                  documents and are unaffected by /legal/*.
                */}
                <Route path="/t/:tenant_slug/privacy" element={<TenantLoader><ConsumerPageContainer><PolicyPage type="privacy" /></ConsumerPageContainer></TenantLoader>} />
                <Route path="/t/:tenant_slug/terms" element={<TenantLoader><ConsumerPageContainer><PolicyPage type="terms" /></ConsumerPageContainer></TenantLoader>} />

                {/*
                  L3 · CARDMOJI's own policies, rendered FROM the Legal Register.
                  One copy of the text, branding supplied by the page, and every
                  published version addressable so an acceptance is provable.
                */}
                <Route path="/legal/:docKey" element={<LegalDocumentPage />} />
                <Route path="/legal/:docKey/v/:version" element={<LegalDocumentPage />} />

                {/* B3/O5 · Cardmoji's own prepaid funnel — program-resolved, no tenant in the URL */}
                <Route path="/create/*" element={<ConsumerFlow />} />
                {/*
                  P7.1 · PUBLIC share landing. The target of every shared link.
                  Deliberately outside ProtectedRoute and TenantLoader: a logged-out
                  recipient from WhatsApp must reach it. Reads presentation-only
                  `card_shares` — never the sender's order.
                */}
                <Route path="/r/:code" element={<SharedCard />} />
                {/* Anonymous support return route. The bearer token arrives in the
                    URL HASH from the emailed link and is stripped on mount. */}
                <Route path="/support/thread" element={S(<SupportThread />)} />
                {/* Cardmoji consumer surface — signed-in account + creator dashboard (designs 9/10). */}
                <Route path="/account" element={<ProtectedRoute><S9Account /></ProtectedRoute>} />
                {/* Entrant waitlist standing — THE one place position/movement renders. */}
                <Route path="/standing" element={<ProtectedRoute><S9bStanding /></ProtectedRoute>} />

                <Route path="/creator" element={<ProtectedRoute><S10Creator /></ProtectedRoute>} />

                {/* Retired mount: /c/<bank slug> was tenant-scoped. Redirect, query preserved. */}
                <Route path="/c/:programSlug/*" element={<LegacyConsumerRedirect />} />


                {/* Credit purchase flow */}
                <Route path="/credits/success" element={<CreditPurchaseSuccess />} />
                <Route path="/credits/cancelled" element={<CreditPurchaseSuccess />} />

                {/* UNIFIED LOGIN — one account, one screen, all surfaces. */}
                <Route path="/login" element={<Login />} />

                {/* Tenant Admin — public auth routes (no sidebar layout).
                    /admin is NOT a second login: it redirects into the unified one. */}
                <Route path="/admin" element={<Navigate to="/login?next=%2Fadmin%2Fdashboard&staff=1" replace />} />
                <Route path="/admin/reset-password" element={S(<AdminResetPassword />)} />
                <Route path="/admin/accept-invite" element={S(<AdminAcceptInvite />)} />

                {/* Authenticated admin routes — wrapped in AdminLayout sidebar shell */}
                <Route element={S(<AdminLayout />)}>
                  <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin>{S(<AdminDashboard />)}</ProtectedRoute>} />
                  <Route path="/admin/review-queue/*" element={<ProtectedRoute requireAdminOrApprover>{S(<AdminReviewQueue />)}</ProtectedRoute>} />
                  <Route path="/admin/review/:id" element={<ProtectedRoute requireAdminOrApprover>{S(<AdminReview />)}</ProtectedRoute>} />
                  <Route path="/admin/card-layout" element={<ProtectedRoute requireAdmin>{S(<AdminCardLayout />)}</ProtectedRoute>} />
                  <Route path="/admin/account" element={<ProtectedRoute>{S(<AdminAccount />)}</ProtectedRoute>} />
                  <Route path="/admin/:tenant_slug/card-layout" element={<ProtectedRoute requireAdmin><TenantLoader>{S(<AdminCardLayout />)}</TenantLoader></ProtectedRoute>} />
                  <Route path="/admin/tenant-config" element={<ProtectedRoute requireAdmin>{S(<AdminTenantConfig />)}</ProtectedRoute>} />
                  <Route path="/admin/config-wizard" element={<ProtectedRoute requireAdmin>{S(<AdminConfigWizard />)}</ProtectedRoute>} />
                  <Route path="/admin/members" element={<ProtectedRoute requireAdmin>{S(<AdminMembers />)}</ProtectedRoute>} />
                  <Route path="/admin/help" element={<ProtectedRoute requireAdmin>{S(<AdminHelpGuide />)}</ProtectedRoute>} />
                </Route>


                {/* Platform Admin (super_admin) — wrapped in PlatformLayout sidebar shell */}
                <Route element={S(<PlatformLayout />)}>
                  {/* §1 Operations dashboard (what needs doing / what is broken).
                      Analytics keeps its own route: volumes and trends never move here. */}
                  <Route path="/platform" element={<Navigate to="/platform/ops" replace />} />
                  <Route path="/platform/dashboard" element={<Navigate to="/platform/ops" replace />} />
                  <Route path="/platform/ops" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformOpsDashboard />)}</ProtectedRoute>} />
                  <Route path="/platform/findings/:key" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformFindings />)}</ProtectedRoute>} />

                  <Route path="/platform/analytics" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformAnalytics />)}</ProtectedRoute>} />

                  {/* §2 Customers (Operations) — the CRM, waitlist and the one support queue */}
                  <Route path="/platform/customers" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformCustomers />)}</ProtectedRoute>} />
                  {/* §2c: enquiries are tickets now (source='website_enquiry'), so this
                      surface is retired the same way the five fake pages were. */}
                  <Route path="/platform/enquiries" element={<Navigate to="/platform/customers?tab=tickets" replace />} />


                  {/* §4 Program config (Configuration) — platform mount of the /admin editors */}
                  <Route path="/platform/program-config" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformProgramConfig />)}</ProtectedRoute>} />
                  <Route path="/platform/config-resolution" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformConfigResolution />)}</ProtectedRoute>} />

                  {/* Leads & allocation — routing rules (INV-ROUTE-5) */}
                  <Route path="/platform/routing-rules" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformRoutingRules />)}</ProtectedRoute>} />


                  {/* §5 Billing (Configuration) */}
                  <Route path="/platform/pricing" element={<ProtectedRoute requireSuperAdmin>{S(<AdminPricing />)}</ProtectedRoute>} />
                  <Route path="/platform/tenant-usage" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformTenantUsage />)}</ProtectedRoute>} />
                  <Route path="/platform/credit-packs" element={<ProtectedRoute requireSuperAdmin>{S(<AdminCreditPacks />)}</ProtectedRoute>} />
                  <Route path="/platform/usage-events" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformUsageEvents />)}</ProtectedRoute>} />

                  {/* §11 Settings (Configuration) */}
                  <Route path="/platform/settings" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformSettings />)}</ProtectedRoute>} />

                  {/* Track C1/C2 — issuer handoff (staff-scoped; RPCs enforce tenant scope) */}
                  <Route path="/platform/handoff" element={<ProtectedRoute>{S(<PlatformHandoff />)}</ProtectedRoute>} />


                  {/* §6 Asset Studio (Content) */}
                  <Route path="/platform/website" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformWebsiteManagement />)}</ProtectedRoute>} />
                  <Route path="/platform/marketing-reel" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformMarketingReel />)}</ProtectedRoute>} />
                  <Route path="/platform/selfies" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformSelfieLibrary />)}</ProtectedRoute>} />
                  <Route path="/platform/selfies/focus" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformSelfieFocus />)}</ProtectedRoute>} />
                  <Route path="/platform/style-gallery" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformStyleGallery />)}</ProtectedRoute>} />
                  {/* Consolidated: gallery-run and style-previews were two surfaces over one job. */}
                  <Route path="/platform/gallery-run" element={<Navigate to="/platform/style-gallery" replace />} />
                  <Route path="/platform/style-previews" element={<Navigate to="/platform/style-gallery" replace />} />
                  <Route path="/platform/content" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformContentGeneration />)}</ProtectedRoute>} />

                  {/* §8 / §9 Templates (Content) */}
                  <Route path="/platform/prompt-templates" element={<ProtectedRoute requireSuperAdmin>{S(<AdminPromptTemplates />)}</ProtectedRoute>} />
                  <Route path="/platform/policy-templates" element={<ProtectedRoute requireSuperAdmin>{S(<AdminPolicyTemplates />)}</ProtectedRoute>} />
                  <Route path="/platform/broadcasts" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformBroadcasts />)}</ProtectedRoute>} />
                  <Route path="/platform/email-templates" element={<ProtectedRoute requireSuperAdmin>{S(<AdminEmailTemplates />)}</ProtectedRoute>} />
                  <Route path="/platform/support-articles" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformSupportArticles />)}</ProtectedRoute>} />

                  {/* §3 Tenants & Access (Governance) */}
                  <Route path="/platform/tenants" element={<ProtectedRoute requireSuperAdmin>{S(<AdminTenants />)}</ProtectedRoute>} />
                  <Route path="/platform/operator-lookup" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformOperatorLookup />)}</ProtectedRoute>} />
                  <Route path="/platform/admin-roster" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformAdminRoster />)}</ProtectedRoute>} />
                  <Route path="/platform/operator-approvals" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformOperatorApprovals />)}</ProtectedRoute>} />

                  {/* §7 Legal register (Governance) — build pending */}
                  <Route path="/platform/legal-register" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformLegalRegister />)}</ProtectedRoute>} />

                  {/* §10 Audit log (Governance) */}
                  <Route path="/platform/audit-log" element={<ProtectedRoute requireSuperAdmin>{S(<PlatformAuditLog />)}</ProtectedRoute>} />

                  {/* Deleted "Acquisition Funnel" stubs — redirect to the real §2 home */}
                  <Route path="/platform/acquisition" element={<Navigate to="/platform/customers" replace />} />
                  <Route path="/platform/activation" element={<Navigate to="/platform/customers" replace />} />
                  <Route path="/platform/success" element={<Navigate to="/platform/customers" replace />} />
                  <Route path="/platform/support" element={<Navigate to="/platform/customers?tab=tickets" replace />} />

                  {/* Workflow IA section landings — nav aliases, no new pages.
                      Old bookmarks above keep working; these are the new names. */}
                  <Route path="/platform/studio" element={<Navigate to="/platform/selfies" replace />} />
                  <Route path="/platform/funnel" element={<Navigate to="/platform/analytics" replace />} />
                  <Route path="/platform/templates" element={<Navigate to="/platform/email-templates" replace />} />
                  <Route path="/platform/knowledge-base" element={<Navigate to="/platform/support-articles" replace />} />
                  <Route path="/platform/leads" element={<Navigate to="/platform/customers?tab=waitlist" replace />} />
                  <Route path="/platform/waitlist" element={<Navigate to="/platform/customers?tab=waitlist" replace />} />
                  <Route path="/platform/allocation" element={<Navigate to="/platform/program-config?tab=reallocation" replace />} />
                  <Route path="/platform/programs" element={<Navigate to="/platform/program-config" replace />} />
                  <Route path="/platform/billing" element={<Navigate to="/platform/pricing" replace />} />



                </Route>


                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </WizardProvider>
        </TenantProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
